'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, ClipboardList, Calendar, Building2, Wrench, Users, Package, BarChart3, Settings, ChevronLeft, ChevronRight, LogOut, Bell,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  badgeVariant?: 'default' | 'warning' | 'danger';
  group?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard',    label: 'Dashboard',          icon: LayoutDashboard, href: '/',                      group: 'principal' },
  { id: 'nav-work-orders',  label: 'Ordens de Serviço',  icon: ClipboardList,   href: '/work-order-management', badge: 7, badgeVariant: 'danger', group: 'operacional' },
  { id: 'nav-schedule',     label: 'Cronograma',         icon: Calendar,        href: '/work-order-management', group: 'operacional' },
  { id: 'nav-buildings',    label: 'Prédios e Espaços',  icon: Building2,       href: '/work-order-management', group: 'cadastro' },
  { id: 'nav-equipment',    label: 'Equipamentos',       icon: Wrench,          href: '/work-order-management', group: 'cadastro' },
  { id: 'nav-teams',        label: 'Equipes e Técnicos', icon: Users,           href: '/work-order-management', group: 'cadastro' },
  { id: 'nav-stock',        label: 'Estoque',            icon: Package,         href: '/work-order-management', badge: 4, badgeVariant: 'warning', group: 'recursos' },
  { id: 'nav-reports',      label: 'Relatórios',         icon: BarChart3,       href: '/work-order-management', group: 'gestao' },
  { id: 'nav-settings',     label: 'Configurações',      icon: Settings,        href: '/work-order-management', group: 'gestao' },
];

const groupLabels: Record<string, string> = {
  principal:   'Principal',
  operacional: 'Operacional',
  cadastro:    'Cadastros',
  recursos:    'Recursos',
  gestao:      'Gestão',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  const groups = navItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const g = item.group || 'outros';
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <aside
      className={`sidebar-transition flex flex-col bg-card border-r border-border shrink-0 relative z-30 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ minHeight: '100vh' }}
    >
      {/* Header */}
      <div className={`flex items-center border-b border-border h-16 ${collapsed ? 'justify-center px-2' : 'px-4 gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-base text-foreground tracking-tight leading-tight">
            CampusMaint
          </span>
        )}
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-[72px] z-40 w-6 h-6 rounded-full bg-card border border-border shadow-card flex items-center justify-center hover:bg-secondary transition-colors duration-150"
        aria-label={collapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} className="text-muted-foreground" />
        ) : (
          <ChevronLeft size={12} className="text-muted-foreground" />
        )}
      </button>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {Object.entries(groups).map(([groupKey, items]) => (
          <div key={`group-${groupKey}`}>
            {!collapsed && (
              <p className="card-label px-2 mb-1.5">{groupLabels[groupKey]}</p>
            )}
            <ul className="space-y-0.5">
              {items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <li key={item.id}>
                    <Link
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                        active
                          ? 'bg-primary/10 text-primary' :'text-secondary-foreground hover:bg-secondary hover:text-foreground'
                      }`}
                    >
                      <Icon
                        size={18}
                        className={`shrink-0 ${active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}
                      />
                      {!collapsed && (
                        <span className="flex-1 truncate">{item.label}</span>
                      )}
                      {!collapsed && item.badge !== undefined && (
                        <span
                          className={`text-xs font-bold px-1.5 py-0.5 rounded-full tabular-nums ${
                            item.badgeVariant === 'danger' ?'bg-red-100 text-red-700'
                              : item.badgeVariant === 'warning' ?'bg-amber-100 text-amber-700' :'bg-secondary text-secondary-foreground'
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge !== undefined && (
                        <span
                          className={`absolute top-0.5 right-0.5 w-4 h-4 text-[9px] font-bold rounded-full flex items-center justify-center tabular-nums ${
                            item.badgeVariant === 'danger' ?'bg-red-500 text-white' :'bg-amber-500 text-white'
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User area */}
      <div className={`border-t border-border p-2 space-y-0.5`}>
        {/* Notifications */}
        <button
          className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium text-secondary-foreground hover:bg-secondary hover:text-foreground transition-all duration-150 relative ${
            collapsed ? 'justify-center' : ''
          }`}
          title={collapsed ? 'Notificações' : undefined}
        >
          <Bell size={18} className="text-muted-foreground shrink-0" />
          {!collapsed && <span className="flex-1 text-left">Notificações</span>}
          <span
            className={`font-bold text-xs bg-red-500 text-white rounded-full flex items-center justify-center tabular-nums ${
              collapsed
                ? 'absolute top-0.5 right-0.5 w-4 h-4 text-[9px]'
                : 'w-5 h-5 text-[10px]'
            }`}
          >
            3
          </span>
        </button>

        {/* User profile */}
        <div
          className={`flex items-center gap-2.5 px-2 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}
        >
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-primary">RC</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-foreground truncate leading-tight">
                Rafael Costa
              </p>
              <p className="text-xs text-muted-foreground truncate">Gestor de Manutenção</p>
            </div>
          )}
          {!collapsed && (
            <Link
              href="/sign-up-login"
              title="Sair"
              className="p-1 rounded hover:bg-secondary transition-colors duration-150"
            >
              <LogOut size={15} className="text-muted-foreground" />
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
}